import { MessageSquare, Trash2 } from "lucide-react"
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"
import { SidebarGlyphSlot } from "@/components/sidebar/sidebar-glyph-slot"

interface Thread {
  id: string
  subject: string
  created_at: string
  updated_at: string
}

interface SidebarThreadItemProps {
  thread: Thread
  onClick: (threadId: string) => void
  onDelete: (threadId: string, event: React.MouseEvent) => void
  isGenerating?: boolean
  menuLayout: "horizontal" | "vertical"
  isCollapsed?: boolean
  isActive?: boolean
}

function formatSubject(subject: string): string {
  return (subject || "Untitled Thread")
    .replace(/^Conversation - /, "")
    .replace(/^Anonymous conversation - /, "")
}

export function SidebarThreadItem({
  thread,
  onClick,
  onDelete,
  isGenerating = false,
  menuLayout,
  isCollapsed = false,
  isActive = false,
}: SidebarThreadItemProps) {
  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          menuLayout={menuLayout}
          onClick={() => onClick(thread.id)}
          tooltip={thread.subject}
          isActive={isActive}
          className="cursor-pointer"
        >
          <SidebarGlyphSlot>
            <MessageSquare className="size-4 shrink-0" />
          </SidebarGlyphSlot>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem className="min-w-0">
      <div className="group relative max-w-full overflow-hidden">
        <SidebarMenuButton
          menuLayout={menuLayout}
          onClick={() => onClick(thread.id)}
          tooltip={thread.subject}
          isActive={isActive}
          className="cursor-pointer w-full min-w-0 overflow-hidden pr-10 items-start !h-auto min-h-8 [&>span:last-child]:!break-words [&>span:last-child]:!whitespace-normal"
        >
          <MessageSquare className="mt-0.5 size-3.5 shrink-0" />
          <span
            className="!break-words !whitespace-normal leading-tight min-w-0 flex-1 text-xs pt-0.5"
            style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
          >
            {isGenerating ? (
              <span className="inline-flex items-center gap-1">
                <span className="animate-pulse bg-muted rounded h-4 flex-1 min-w-16" />
                <span className="text-xs text-muted-foreground animate-pulse">
                  naming...
                </span>
              </span>
            ) : (
              formatSubject(thread.subject)
            )}
          </span>
        </SidebarMenuButton>
        <button
          onClick={(e) => onDelete(thread.id, e)}
          className="absolute right-2 top-2 z-10 p-1 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100"
          title="Delete thread"
          aria-label="Delete thread"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </SidebarMenuItem>
  )
}
